import React, { useState } from "react";
import { Copy, Mail, Check, X, ExternalLink } from "lucide-react";

const InstantVideoCallModal = ({ callData, onClose, onSendEmail }) => {
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!callData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No call data available</p>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    );
  }

  const { candidateInviteLinks, interviewerInviteLink, interviewDetails, roomId } = callData;
  const candidateLink = candidateInviteLinks?.[0];
  const candidate = interviewDetails?.candidates?.[0];

  const handleCopyLink = async () => {
    if (candidateLink?.inviteLink) {
      try {
        await navigator.clipboard.writeText(candidateLink.inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy link:", err);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = candidateLink.inviteLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleSendEmail = async () => {
    if (!candidate?.email || !candidateLink?.candidateId) {
      return;
    }

    setIsLoading(true);
    try {
      await onSendEmail(roomId, candidateLink.candidateId, candidate.email);
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinInterview = () => {
    if (interviewerInviteLink) {
      window.open(interviewerInviteLink, "_blank");
    }
  };

  return (
    <div className="p-6 max-w-md mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Instant Video Call Link
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded-full transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Candidate Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-2">Candidate</h3>
          <p className="text-gray-600">{candidate?.name || "Unknown"}</p>
          <p className="text-sm text-gray-500">{candidate?.email || "No email"}</p>
        </div>

        {/* Interview Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-2">Interview Details</h3>
          <p className="text-gray-600">
            <strong>Job:</strong> {interviewDetails?.jobTitle || "General Interview"}
          </p>
          <p className="text-gray-600">
            <strong>Interviewer:</strong> {interviewDetails?.owner?.name || "Unknown"}
          </p>
          <p className="text-sm text-gray-500">
            <strong>Type:</strong> {callData.interviewType || "Technical"}
          </p>
        </div>

        {/* Link Display */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-2">Candidate Interview Link</h3>
          <p className="text-sm text-gray-600 mb-2">Share this link with the candidate to join the interview</p>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={candidateLink?.inviteLink || ""}
              readOnly
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-white"
            />
            <button
              onClick={handleCopyLink}
              className={`p-2 rounded transition-colors ${
                copied
                  ? "bg-green-500 text-white"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
              title="Copy candidate link"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          {copied && (
            <p className="text-green-600 text-sm mt-2">Candidate link copied to clipboard!</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleJoinInterview}
            className="flex-1 flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors"
          >
            <ExternalLink size={16} />
            <span>Join as Interviewer</span>
          </button>

          {candidate?.email && (
            <button
              onClick={handleSendEmail}
              disabled={isLoading || emailSent}
              className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors ${
                emailSent
                  ? "bg-green-500 text-white"
                  : isLoading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {emailSent ? (
                <>
                  <Check size={16} />
                  <span>Sent!</span>
                </>
              ) : isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail size={16} />
                  <span>Send Email</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-medium text-yellow-800 mb-2">Instructions</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Copy the candidate link above and share it with the candidate</li>
            <li>• Or send it directly via email using the "Send Email" button</li>
            <li>• Use "Join as Interviewer" to join the interview yourself</li>
            <li>• Both you and the candidate can join immediately</li>
            <li>• The interview will be recorded automatically</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InstantVideoCallModal;
